import { LoginTypes } from '@api/login';
import { registerUser } from '@api/users';
import Card from '@components/Atoms/Card';
import DefaultButton from '@components/Atoms/DefaultButton';
import Img from '@components/Atoms/Img';
import LinkText from '@components/Atoms/Link';
import SignUp from '@components/Organisms/SignUp';
import { LoginMethods } from '@enums/LoginMethods';
import { buildCheckYourInboxPath } from '@helpers/auth.helper';
import useIsMobileApp from '@hooks/useIsMobileApp';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { CompanyLoginMethodTypesInterface } from '@interfaces/index';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { showToast } from '@shared/atoms';
import { LoadingBackdrop } from '@shared/molecules';
import { ToastMessages } from 'constants/toastMessages';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

const Registration: React.FunctionComponent<RegistrationProps> = (props) => {
  const {
    content,
    organizationLogo,
    organizationAltText,
    organizationId,
    companyURL,
    hideHapstarLogo,
    platformName,
  } = props;
  const { isMobile } = useWindowDimensions();
  const isMobileApp = useIsMobileApp();
  const [loginMethodsToDisplay, setLoginMethodsToDisplay] = useState<
    CompanyLoginMethodTypesInterface[] | []
  >([]);
  const [userRegDetails, setUserRegDetails] = useState<{
    email: string;
    isAgree: boolean;
    companyId: number;
  } | null>(null);
  const [loadingLoginRequest, setLoadingLoginRequest] = useState(false);
  const history = useHistory();
  const type = isMobileApp ? LoginTypes.TOKEN : LoginTypes.LINK;

  const handleOnBack = async (): Promise<void> => {
    setLoginMethodsToDisplay([]);
  };

  const sendLoginMagicRequest = async (email: string | undefined): Promise<void> => {
    setLoadingLoginRequest(true);

    if (!email) {
      showToast(ToastMessages.auth.info.PLEASE_ENTER_A_VALID_EMAIL_ADDRESS, undefined, 'warning');
      setLoadingLoginRequest(false);

      return;
    }

    try {
      const response = await registerUser({
        email,
        isAgree: userRegDetails?.isAgree,
        type,
        companyId: userRegDetails?.companyId,
      });

      if (response.ok) {
        showToast(ToastMessages.auth.info.EMAIL_SENT_TO_YOUR_INBOX, undefined, 'info');
        navigateToCheckInbox(email);
      } else if (response.status === 400 || response.status === 403) {
        showToast(ToastMessages.auth.info.WE_COULD_NOT_RECOGNISE_THIS_EMAIL, undefined, 'warning');
      } else if (response?.status === 409) {
        showToast(
          ToastMessages.auth.info.EMAIL_ADDRESS_IS_ALREADY_REGISTERED,
          undefined,
          'warning',
        );
      } else {
        showToast(ToastMessages.auth.error.LOGIN_UNSUCCESSFUL_CONTACT_ADMIN, undefined, 'error');
      }
    } catch (error) {
      showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
    } finally {
      setLoadingLoginRequest(false);
    }
  };

  const navigateToCheckInbox = (email: string): void => {
    history.push(
      buildCheckYourInboxPath(email, {
        companyURL,
        isMobileApp,
      }),
    );
  };

  const handleLoginMethodClick = async (
    loginMethod: CompanyLoginMethodTypesInterface,
  ): Promise<void> => {
    const { loginMethod: method, loginUrl } = loginMethod;
    const isSSO = method === LoginMethods.SSO;
    const isLink = method === LoginMethods.LINK;

    if (isMobileApp) {
      await sendLoginMagicRequest(userRegDetails?.email);
    } else if (!isMobileApp) {
      if (isSSO) {
        // setLoadingLoginRequest(true);

        try {
          const response = await registerUser({
            email: userRegDetails?.email,
            isAgree: userRegDetails?.isAgree,
            type: LoginTypes.SSO,
            companyId: userRegDetails?.companyId,
          });

          if (response.ok) {
            showToast(ToastMessages.auth.info.REDIRECTING_TO_SINGLE_SIGN_ON, undefined, 'info');
            window.location.replace(loginUrl);
          } else if (response.status === 400 || response.status === 403) {
            showToast(
              ToastMessages.auth.info.WE_COULD_NOT_RECOGNISE_THIS_EMAIL,
              undefined,
              'warning',
            );
          } else if (response?.status === 409) {
            showToast(
              ToastMessages.auth.info.EMAIL_ADDRESS_IS_ALREADY_REGISTERED,
              undefined,
              'warning',
            );
          } else {
            showToast(
              ToastMessages.auth.error.LOGIN_UNSUCCESSFUL_CONTACT_ADMIN,
              undefined,
              'error',
            );
          }
        } catch (error) {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
        } finally {
          // setLoadingLoginRequest(false);
        }
      } else if (isLink) {
        await sendLoginMagicRequest(userRegDetails?.email);
      } else {
        // Default case - send magic link
        await sendLoginMagicRequest(userRegDetails?.email);
      }
    }
  };

  const handleLoginMethodsToDisplay = (
    methods: CompanyLoginMethodTypesInterface[],
    userDetails: {
      email: string;
      isAgree: boolean;
      companyId: number;
    } | null,
  ): void => {
    setLoginMethodsToDisplay(methods);

    if (userDetails) {
      setUserRegDetails(userDetails);
    } else {
      setUserRegDetails(null);
      showToast(ToastMessages.auth.info.PLEASE_ENTER_A_VALID_EMAIL_ADDRESS, undefined, 'warning');
    }
  };

  return (
    <AuthenticationLayout
      title="SignUp"
      organizationLogo={organizationLogo}
      organizationAltText={organizationAltText}
      hideHapstarLogoEnabled={hideHapstarLogo}
    >
      {loadingLoginRequest && <LoadingBackdrop text="Preparing your magic link..." />}

      <div className={styles.cardContainer}>
        <Card>
          <div className={styles.registrationContentContainer}>
            <div className={styles.registrationContent}>
              {loginMethodsToDisplay && loginMethodsToDisplay?.length > 1 ? (
                <div className={styles.content}>
                  <h1>Select Login option</h1>
                  {loginMethodsToDisplay.map((login, index) => (
                    <div className={styles.buttonHolder} key={index}>
                      <DefaultButton
                        variant="primary"
                        type="button"
                        onClick={() => handleLoginMethodClick(login)}
                        disabled={loadingLoginRequest}
                      >
                        {login.loginMethodText}
                      </DefaultButton>
                    </div>
                  ))}

                  <div className={styles.goBack}>
                    <LinkText onClick={handleOnBack}>Back</LinkText>
                  </div>
                </div>
              ) : (
                <>
                  {organizationLogo && isMobile && !hideHapstarLogo && (
                    <div className={styles.organizationLogoHolder}>
                      <Img
                        alt={organizationAltText || 'Organization logo'}
                        src={organizationLogo}
                        className={styles.img}
                      />
                    </div>
                  )}
                  <h1>{platformName ? `Sign-up to ${platformName}` : 'Sign-up to Hapstar'}</h1>
                  <div className={styles.registrationDetails}>
                    <SignUp
                      content={content}
                      organizationId={organizationId}
                      companyURL={companyURL}
                      handleLoginMethodsToDisplay={handleLoginMethodsToDisplay}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>
    </AuthenticationLayout>
  );
};

Registration.displayName = 'Registration';

Registration.defaultProps = {
  organizationLogo: undefined,
  organizationAltText: undefined,
  organizationId: undefined,
  companyURL: undefined,
  hideHapstarLogo: false,
  platformName: null,
};

interface RegistrationProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  content: JSX.Element | string | undefined;
  organizationLogo?: string | undefined;
  organizationAltText?: string | undefined;
  organizationId?: number;
  companyURL?: string;
  hideHapstarLogo?: boolean;
  platformName?: string | null;
}

export default Registration;
